import React, { useEffect, useRef, useState } from "react";
import { IoClose } from "react-icons/io5";
import { Document, Page, pdfjs } from "react-pdf";
import { fetchBistroNwt } from "../../lib/cms.ts";

// PDF worker (verze musí sedět s nainstalovaným pdfjs-dist)
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type Props = {
  open: boolean;
  onClose: () => void;
};

const DenniNabidkaModal: React.FC<Props> = ({ open, onClose }) => {
  const [rablinu, setRablinu] = useState<{ pdf: string | null; text: string }>({
    pdf: null,
    text: "",
  });
  const [loaded, setLoaded] = useState(false);
  const [numPages, setNumPages] = useState(0);
  const [pdfError, setPdfError] = useState(false);
  const [width, setWidth] = useState<number>(0);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  // Data z Sanity (BistroNwt) – stejný zdroj jako stránka /denni-nabidka
  useEffect(() => {
    if (!open || loaded) return;
    fetchBistroNwt()
      .then((data) => {
        setRablinu({
          pdf: data?.pdfRablinuUrl || null,
          text: data?.textRablinu || "",
        });
        setLoaded(true);
      })
      .catch(console.error);
  }, [open, loaded]);

  // Zamknout scroll pozadí + zavřít Escapem
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  // Šířka stránky PDF podle šířky modalu (responsivní)
  useEffect(() => {
    if (!open) return;
    const measure = () => {
      if (bodyRef.current) setWidth(bodyRef.current.clientWidth);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [open, loaded]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Denní nabídka"
    >
      <div
        className="relative bg-[#F5E7CC] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 bg-[#60260F] text-white shrink-0">
          <h2 className="text-lg md:text-xl font-serif font-bold">Denní nabídka</h2>
          <button
            onClick={onClose}
            aria-label="Zavřít"
            className="text-3xl leading-none hover:opacity-80 transition"
          >
            <IoClose />
          </button>
        </div>

        <div ref={bodyRef} className="flex-1 overflow-auto p-3 md:p-4 flex flex-col gap-3">
          {rablinu.text ? (
            <p className="text-sm md:text-base text-[#32180d] leading-relaxed">
              {rablinu.text}
            </p>
          ) : null}

          {!loaded ? (
            <p className="text-center text-[#60260F] py-10">Načítám…</p>
          ) : rablinu.pdf && !pdfError ? (
            <>
              <Document
                file={rablinu.pdf}
                onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                onLoadError={() => setPdfError(true)}
                loading={<p className="text-center text-[#60260F] py-10">Načítám PDF…</p>}
              >
                {Array.from({ length: numPages }, (_, i) => (
                  <Page
                    key={i}
                    pageNumber={i + 1}
                    width={width || undefined}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    className="mb-3 rounded-lg overflow-hidden shadow"
                  />
                ))}
              </Document>

              {/* Záloha, kdyby chtěl uživatel PDF stáhnout/otevřít celé */}
              <a
                href={rablinu.pdf}
                target="_blank"
                rel="noopener noreferrer"
                className="text-center text-sm text-[#60260F] underline py-2"
              >
                Otevřít PDF v nové kartě
              </a>
            </>
          ) : rablinu.pdf && pdfError ? (
            // Fallback, kdyby se render nepovedl
            <a
              href={rablinu.pdf}
              target="_blank"
              rel="noopener noreferrer"
              className="text-center bg-[#60260F] text-white py-3 rounded-xl hover:bg-[#7a3419] transition font-serif"
            >
              Otevřít PDF
            </a>
          ) : (
            <p className="text-center text-[#60260F] py-10">
              Denní nabídka není momentálně k dispozici.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default DenniNabidkaModal;
